// main.tsx -- Trippi Charge standalone mount.
// Locale is parsed from the URL path (/fi/charge/, /en-us/charge/).
// The arcade-side string bundle (src/i18n/) is layered ON TOP of the legacy
// in-game bundle (trippiCharge.i18n.en), so any key not yet migrated falls
// back to English and renders unchanged. Run-by-run migration is safe.
import { getStrings, localeFromPath } from "./i18n";
import { type ArcadeMsgKey, initGameI18n, t } from "./games/shared/i18n";
import { assetUrl } from "./games/shared/asset-base";
import { isBuggiesPath } from "./games/buggies/boot";
import { installTouchGuards } from "@shell/touch-guards";
import "@shell/theme.css";
import "@shell/arcade-skin.css";

installTouchGuards();

const appRoot = document.getElementById("app");
if (!appRoot) throw new Error("Missing #app element");

// Localized boot-failure card (game chunk or asset load failed). The arcade
// i18n falls back to English per key, so this renders even when the locale
// dictionary itself failed to load.
function bootErrorCard(msgKey: ArcadeMsgKey): void {
  appRoot!.innerHTML =
    `<div style="position:fixed;inset:0;display:flex;align-items:center;` +
    `justify-content:center;font-family:system-ui">${t(msgKey)} ` +
    `<button onclick="location.reload()" style="margin-left:8px">` +
    `${t("err.retry")}</button></div>`;
}

// Path-based dispatch. Only the /charge path boots the game bundle --
// everything else (including the site root /) shows a minimal link
// instead. This keeps the root a proper stub during the testing phase
// and lets a real launcher replace it later without touching the game
// code. Matches /charge, /charge/, /<locale>/charge, /<locale>/charge/.
// Strip the build base ("/", "/trippi/") from the pathname before the
// game-segment match so the same regexes work for both the standalone
// trippi.app root build and the tenant.tld/trippi/ build. BASE_URL always
// carries a trailing slash; the result keeps a leading "/".
const BASE_URL =
  (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ??
    "/";
const basePrefix = BASE_URL.replace(/\/+$/, "");
const rawPath = window.location.pathname;
// On a same-origin tenant mount the browser URL keeps the LOCALIZED prefix
// (e.g. /pelit/drift) while the build base is /trippi -- Caddy only rewrites
// the upstream request path, not the address bar. The server stamps the public
// prefix into window.__TRIPPI_PREFIX__ so we strip it here; otherwise the game
// segment never matches and we fall through to the menu. Falls back to the
// baked base for the trippi.app root and the /trippi mount.
const publicPrefix =
  (window as unknown as { __TRIPPI_PREFIX__?: string }).__TRIPPI_PREFIX__;
const effectivePrefix = publicPrefix && rawPath.startsWith(publicPrefix)
  ? publicPrefix
  : basePrefix;
const gamePath = effectivePrefix && rawPath.startsWith(effectivePrefix)
  ? rawPath.slice(effectivePrefix.length) || "/"
  : rawPath;

const isChargePath = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?charge(?:\/|$)/i
  .test(gamePath);
const isDriftPath = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?drift(?:\/|$)/i
  .test(gamePath);
/**
 * Pulukart is the drift game in a kart body -- same engine, same tracks, same
 * leaderboards, same gamertags, only the car and the wordmark differ (see
 * games/drift/vehicle-profile.ts). It is not a separate URL: the TENANT
 * same-origin build is Pulukart, and the trippi.app root build stays Trippi
 * Drift, so reittikartta.net/pelit/drift and trippi.app/drift are the same
 * URLs shape with a different car at each origin.
 *
 * Keyed on the baked build base (ARCADE_BASE=/trippi/ is set only for the
 * tenant image, alongside ARCADE_PREFIX for its server) rather than on the
 * per-request X-Trippi-Prefix header, so the client and the server-rendered
 * head cannot disagree and show a Pulukart car under a Trippi Drift title.
 */
const IS_TENANT_MOUNT = basePrefix !== "";
const isHeliPath = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?heli(?:\/|$)/i
  .test(gamePath);
const isCharge3dPath = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?charge3d(?:\/|$)/i
  .test(gamePath);
const isMiniPath = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?minigolf(?:\/|$)/i
  .test(gamePath);
const isMegaPath = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?megagolf(?:\/|$)/i
  .test(gamePath);
const isMazePath = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?maze(?:\/|$)/i
  .test(gamePath);
const isGolfPath = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?golf(?:\/|$)/i
  .test(gamePath);
const isRallyPath = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?rally(?:\/|$)/i
  .test(gamePath);
const isTagPath = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?tag(?:\/|$)/i
  .test(gamePath);
const isBuggiesGame = isBuggiesPath(gamePath);
const isSlotsPath = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?slots(?:\/|$)/i
  .test(gamePath);

if (isRallyPath) {
  // Rally boots via dynamic import so three.js never loads elsewhere.
  document.querySelectorAll(".arc-shell").forEach((el) => el.remove());
  appRoot.style.cssText = "position:fixed;inset:0;overflow:hidden;";
  // ?stage=<slug> picks any stage manifest from /tracks/rally/ (e.g.
  // koli-hill, kaalikontie-hameenkyro). Slug-validated; default unchanged.
  const stageParam = new URLSearchParams(location.search).get("stage");
  const stage = stageParam && /^[a-z0-9-]{1,64}$/.test(stageParam) ? stageParam : "myllypuro-myyrmaki";
  Promise.all([import("./games/rally/index"), initGameI18n()]).then(([{ mountRally }]) => {
    mountRally(appRoot, assetUrl(`/tracks/rally/${stage}.v1.json`)).catch((err) => {
      bootErrorCard("err.loadStage");
      console.error("rally mount failed", err);
    });
  });
} else if (isMiniPath) {
  // Pulu Minigolf boots via dynamic import so three.js never loads elsewhere.
  document.querySelectorAll(".arc-shell").forEach((el) => el.remove());
  appRoot.style.cssText = "position:fixed;inset:0;overflow:hidden;";
  // Everything after the /minigolf/ segment is the pretty entry -- a course
  // slug -- so a link opens straight into that course, the way /drift/ takes
  // a track code.
  const miniSubPath = gamePath.replace(
    /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?minigolf\/?/i,
    "",
  );
  Promise.all([import("./games/minigolf/index"), initGameI18n()]).then(({ 0: m }) => {
    m.mountMinigolf(
      appRoot,
      assetUrl("/tracks/minigolf/helsinki-9.v1.json"),
      miniSubPath,
      {
        // Follow the ball, do not box the camera in. Measured on chester-9 with
        // the clamp on: the worst offset from screen centre over four holes was
        // 0.40, 1.31, 0.34, 0.36 view-widths, where 0.5 is already the edge --
        // so the ball left the screen entirely on one hole. Hangover has passed
        // false since it shipped, which is why its tracking has always felt
        // better on the same engine.
        //
        // The clamp exists to hide the void past a small hand-placed loop's
        // edge. These courses stream city tiles in every direction, and the
        // generated CityGolf ones will more so, so there is no void left to
        // hide.
        clampCameraToCourse: false,
        // The tour page's iframe says so. A page that has already printed the
        // city's name, its prose and its nine stops wants the board, not a
        // second copy of its own card -- see hudSignals.embed.
        embed: new URLSearchParams(location.search).get("embed") === "1",
      },
    ).catch((err) => {
      bootErrorCard("err.loadGame");
      console.error("minigolf mount failed", err);
    });
  });
} else if (isMazePath) {
  // Pulu Maze boots via dynamic import so three.js never loads elsewhere.
  document.querySelectorAll(".arc-shell").forEach((el) => el.remove());
  appRoot.style.cssText = "position:fixed;inset:0;overflow:hidden;";
  Promise.all([import("./games/maze/index"), initGameI18n()]).then(({ 0: m }) => {
    m.mountMaze(appRoot).catch((err) => {
      bootErrorCard("err.loadGame");
      console.error("maze mount failed", err);
    });
  });
} else if (isGolfPath) {
  // Pulu Golf boots via dynamic import so three.js never loads on other paths.
  document.querySelectorAll(".arc-shell").forEach((el) => el.remove());
  appRoot.style.cssText = "position:fixed;inset:0;overflow:hidden;";
  Promise.all([import("./games/golf/index"), initGameI18n()]).then(([{ mountGolf }]) => {
    mountGolf(appRoot).catch((err) => {
      bootErrorCard("err.loadGame");
      console.error("golf mount failed", err);
    });
  });
} else if (isHeliPath) {
  // Heli boots via dynamic import so three.js never loads on other paths.
  document.querySelectorAll(".arc-shell").forEach((el) => el.remove());
  appRoot.style.cssText = "position:fixed;inset:0;overflow:hidden;";
  Promise.all([import("./games/heli/index"), initGameI18n()]).then(([{ mountHeli }]) => {
    mountHeli(appRoot).catch((err) => {
      bootErrorCard("err.loadGame");
      console.error("heli mount failed", err);
    });
  });
} else if (isCharge3dPath) {
  // Prototype: Trippi Charge core loop ported onto engine3d. Lazy import
  // keeps the three.js bundle off /charge and the stub page.
  document.querySelectorAll(".arc-shell").forEach((el) => el.remove());
  appRoot.style.cssText = "position:fixed;inset:0;overflow:hidden;";
  Promise.all([import("./games/charge3d/index"), initGameI18n()]).then(([{ mountCharge3d }]) => {
    mountCharge3d(appRoot, assetUrl("/tracks/charge3d/demo-route.v1.json")).catch((err) => {
      bootErrorCard("err.loadRoute");
      console.error("charge3d mount failed", err);
    });
  });
} else if (isTagPath) {
  // Trippi Tag (look-around flight tethered to the car) boots via dynamic
  // import so three.js never loads on other paths.
  document.querySelectorAll(".arc-shell").forEach((el) => el.remove());
  appRoot.style.cssText = "position:fixed;inset:0;overflow:hidden;";
  Promise.all([import("./games/tag/index"), initGameI18n()]).then(([{ mountTag }]) => {
    mountTag(appRoot).catch((err) => {
      bootErrorCard("err.loadGame");
      console.error("tag mount failed", err);
    });
  });
} else if (isBuggiesGame) {
  // Buggies boots via dynamic import so the three.js bundle never loads on
  // other paths. trackUrl is a centerline source only (the map is not drawn);
  // we reuse drift's kerava-loop manifest as the bundled default.
  document.querySelectorAll(".arc-shell").forEach((el) => el.remove());
  appRoot.style.cssText = "position:fixed;inset:0;overflow:hidden;";
  Promise.all([import("./games/buggies/index"), initGameI18n()]).then(([{ mountBuggies }]) => {
    mountBuggies(appRoot, assetUrl("/tracks/drift/kerava-loop.v1.json")).catch((err) => {
      bootErrorCard("err.loadTrack");
      console.error("buggies mount failed", err);
    });
  });
} else if (isDriftPath) {
  // Drift boots via dynamic import so the three.js bundle (vendor-three)
  // never loads on /charge or the stub page.
  document.querySelectorAll(".arc-shell").forEach((el) => el.remove());
  appRoot.style.cssText = "position:fixed;inset:0;overflow:hidden;";
  // Everything after the /drift/ segment is the pretty entry (a track code or
  // "here"); pass it to the game so it can open straight into context.
  const driftSubPath = gamePath.replace(
    /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?drift\/?/i,
    "",
  );
  // Tenant origins race the kart, trippi.app races the saloon. ?car= still
  // overrides either way (resolveVehicleProfile).
  const vehicleDefault = IS_TENANT_MOUNT ? "kart" : "drift";
  Promise.all([import("./games/drift/index"), initGameI18n()]).then(([{ mountDrift }]) => {
    mountDrift(
      appRoot,
      assetUrl("/tracks/drift/kerava-loop.v1.json"),
      driftSubPath,
      vehicleDefault,
    ).catch((err) => {
      bootErrorCard("err.loadTrack");
      console.error("drift mount failed", err);
    });
  });
} else if (isSlotsPath) {
  // Slots boots via dynamic import so the three.js bundle never loads elsewhere.
  document.querySelectorAll(".arc-shell").forEach((el) => el.remove());
  appRoot.style.cssText = "position:fixed;inset:0;overflow:hidden;";
  Promise.all([import("./games/slots/index"), initGameI18n()]).then(([{ mountSlots }]) => {
    mountSlots(appRoot).catch((err) => {
      bootErrorCard("err.loadGame");
      console.error("slots mount failed", err);
    });
  });
} else if (isMegaPath) {
  // Megagolf boots via dynamic import so three.js never loads elsewhere.
  document.querySelectorAll(".arc-shell").forEach((el) => el.remove());
  appRoot.style.cssText = "position:fixed;inset:0;overflow:hidden;";
  // Trailing segment picks the hole: /megagolf/3 is the fourth leg.
  const megaSub = gamePath.replace(
    /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?megagolf\/?/i,
    "",
  );
  const holeIndex = /^\d{1,2}$/.test(megaSub) ? Number(megaSub) : 0;
  Promise.all([import("./games/megagolf/index"), initGameI18n()]).then(({ 0: m }) => {
    m.mountMegagolf(appRoot, holeIndex).catch((err) => {
      bootErrorCard("err.loadGame");
      console.error("megagolf mount failed", err);
    });
  });
} else if (!isChargePath) {
  // Completely blank stub: drop the arc-shell green-dice backdrop that
  // index.html paints pre-SPA, paint a plain white canvas, and show
  // just the link. Testing-phase placeholder -- a real launcher will
  // replace this once other games join the arcade shell.
  document.querySelectorAll(".arc-shell").forEach((el) => el.remove());
  document.body.style.background = "#ffffff";
  appRoot.innerHTML =
    `<div style="position:fixed;inset:0;display:flex;align-items:center;` +
    `justify-content:center;font-family:Manrope,system-ui,sans-serif;` +
    `background:#ffffff;">` +
    `<a href="${assetUrl("/charge/")}" style="color:#1a1a1a;font-size:20px;` +
    `text-decoration:underline;">Play Trippi Charge</a>` +
    `<a href="${assetUrl("/drift/")}" style="color:#1a1a1a;font-size:20px;` +
    `text-decoration:underline;margin-left:24px;">Play Drift</a>` +
    `<a href="${assetUrl("/rally/")}" style="color:#1a1a1a;font-size:20px;` +
    `text-decoration:underline;margin-left:24px;">Play Rally</a>` +
    `<a href="${assetUrl("/heli/")}" style="color:#1a1a1a;font-size:20px;` +
    `text-decoration:underline;margin-left:24px;">Play Heli</a>` +
    `<a href="${assetUrl("/charge3d/")}" style="color:#1a1a1a;font-size:20px;` +
    `text-decoration:underline;margin-left:24px;">Play Charge3D</a>` +
    `<a href="${assetUrl("/tag/")}" style="color:#1a1a1a;font-size:20px;` +
    `text-decoration:underline;margin-left:24px;">Play Trippi Tag</a>` +
    `<a href="${assetUrl("/buggies/")}" style="color:#1a1a1a;font-size:20px;` +
    `text-decoration:underline;margin-left:24px;">Play Trippi Buggies</a>` +
    `<a href="${assetUrl("/slots/")}" style="color:#1a1a1a;font-size:20px;` +
    `text-decoration:underline;margin-left:24px;">Play Slots</a></div>`;
} else {
  // Leave #app transparent so the inline arc-shell backdrop (green +
  // rotated dice) painted by index.html stays visible during the preload
  // flash and remains as the backdrop until the map canvas + splash card
  // cover it. An opaque background here would force a dark-flash moment
  // the instant main.tsx runs.
  appRoot.style.cssText =
    "position:fixed;inset:0;overflow:hidden;background:transparent;";

  // Charge boots via dynamic import like every other game so its bundle
  // (incl. maplibre-gl, ~220 KB brotli / 1 MB parse) never loads on
  // /drift, /rally, /tag, /heli, /charge3d or the stub page. The
  // arc-shell backdrop covers the brief extra load moment.
  import("@games/charge").then(({ trippiCharge }) => {
    const locale = localeFromPath();
    const arcade = getStrings(locale) as unknown as Record<string, string>;
    const fallback = trippiCharge.i18n.en as unknown as Record<string, string>;

    const ctx = {
      root: appRoot,
      locale,
      t: (key: string, params?: Record<string, string | number>) => {
        let val = arcade[key] ?? fallback[key] ?? key;
        if (params) {
          for (const [k, v] of Object.entries(params)) {
            val = val.replace(`{${k}}`, String(v));
          }
        }
        return val;
      },
    };

    trippiCharge.mount(ctx);
  }).catch((err) => {
    appRoot.innerHTML =
      `<div style="position:fixed;inset:0;display:flex;align-items:center;` +
      `justify-content:center;font-family:system-ui">Failed to load game. ` +
      `<button onclick="location.reload()" style="margin-left:8px">Retry` +
      `</button></div>`;
    console.error("charge mount failed", err);
  });
}
